import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import SubregionMap from '../SubregionMap';
import AssignToSubregionPopover from '../AssignToSubregionPopover';
import SubregionPanel from '../panel/SubregionPanel';
import { useSubregionDraft, useSyncPanelDirty } from '../panel/hooks';
import AddressRadiusModal from './AddressRadiusModal';
import CarriersSection from './CarriersSection';
import { useCarrierRegions } from './hooks';
import { unitToMeters, findZipsInRadius, getCircleBounds } from './utils';

const MapColumn = ({ state, loading, callbacks, carriersForLeg, creating, onDirtyChange, onClose, onSaved }) => {
    const mapRef = useRef(null);
    const [root, setRoot] = useState(null);
    const initRoot = useCallback((node) => {
        if (node !== null) setRoot(node);
    }, []);

    const [radiusInputOpen, setRadiusInputOpen] = useState(false);

    const panelOpen = creating || !!state.selectedSubregion;

    // Here rather than in the panel because the map paints from it too.
    const draft = useSubregionDraft({
        subregion: state.selectedSubregion || null,
        isNew: creating,
        callbacks,
        onSaved,
    });

    // Mirrored up so the guard can read it synchronously on a click, without the draft living
    // above this component and re-rendering the subregion list on every keystroke.
    useSyncPanelDirty(onDirtyChange, panelOpen && draft.isDirty);

    // The carrier card under the pointer, whose own regions the map overlays. Read through the open
    // panel's carriers, so a card that closes or is removed mid-hover can't leave its regions on the map.
    const [hoveredCarrierId, setHoveredCarrierId] = useState(null);
    const panelCarrierIds = panelOpen ? draft.carriers.map((mapping) => mapping.carrier_id) : [];
    const regionZipsByCarrier = useCarrierRegions(panelCarrierIds);
    const activeCarrierId = panelCarrierIds.includes(hoveredCarrierId) ? hoveredCarrierId : null;
    const carrierRegionZips = useMemo(
        () => (activeCarrierId && regionZipsByCarrier[activeCarrierId]) || [],
        [activeCarrierId, regionZipsByCarrier]
    );

    const handleRadiusConfirm = ({ center, minRadius, maxRadius, unit }) => {
        const minMeters = unitToMeters(minRadius, unit);
        const maxMeters = unitToMeters(maxRadius, unit);
        setRadiusInputOpen(false);
        if (!mapRef.current) return;
        mapRef.current.fitBounds(getCircleBounds(center, maxMeters), { padding: 80 });
        mapRef.current.once('idle', () => {
            const zips = findZipsInRadius(mapRef.current, center, minMeters, maxMeters);
            if (zips.length === 0) return;
            callbacks.selectObject((prev) => {
                const next = { ...(prev.zip || {}) };
                for (const zip of zips) {
                    next[zip] = true;
                }
                return { ...prev, zip: next };
            });
        });
    };

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex: 1;
                flex-basis: 0;
                overflow: hidden;
                position: relative;
            `}
        >
            <div
                ref={initRoot}
                css={css`
                    width: 100%;
                    height: 100%;
                    display: flex;
                `}
            />
            <SubregionMap
                ref={mapRef}
                node={root}
                subregions={state.subregions}
                callbacks={{ ...callbacks, onLoad: () => {} }}
                selectedSubregion={state.selectedSubregion}
                hoveredSubregion={state.hoveredSubregion}
                selectedZips={state.selectedZips}
                hoveredSubregionZip={state.hoveredSubregionZip}
                radiusControl={{
                    callbacks: {
                        onOpen: () => setRadiusInputOpen(true),
                    },
                }}
                zipToggle={panelOpen ? { zips: draft.zips, onToggle: draft.toggle } : null}
                carrierRegionZips={carrierRegionZips}
            />
            <AddressRadiusModal
                open={radiusInputOpen}
                onClose={() => setRadiusInputOpen(false)}
                onConfirm={handleRadiusConfirm}
            />
            {panelOpen ? (
                <SubregionPanel
                    subregion={state.selectedSubregion || null}
                    subregions={state.subregions}
                    callbacks={callbacks}
                    draft={draft}
                    onClose={onClose}
                    carriers={
                        <CarriersSection
                            isNew={creating}
                            draft={draft}
                            carriersForLeg={carriersForLeg}
                            allCarriers={state.allCarriers}
                            hoveredCarrierId={activeCarrierId}
                            regionZipsByCarrier={regionZipsByCarrier}
                            onCarrierHover={setHoveredCarrierId}
                        />
                    }
                />
            ) : null}
            {state.selectedZips.length > 0 ? (
                <AssignToSubregionPopover
                    loading={loading}
                    subregions={state.subregions}
                    selectedZips={state.selectedZips}
                    callbacks={callbacks}
                />
            ) : null}
        </Grid>
    );
};

export default MapColumn;
